import {
  Action,
  applyMiddleware,
  combineReducers,
  compose,
  createStore,
  ReducersMapObject,
  Store
} from 'redux'
import createSagaMiddleware from 'redux-saga'
import { persistStore, persistReducer, PersistConfig } from 'redux-persist'
import { all } from 'redux-saga/effects'

import { AirState } from './ducks/air/types'
import { ApprovalsState } from './ducks/approvals/types'
import { AuditsState } from './ducks/audits/types'
import { AuthState } from './ducks/auth/types'
import { CitiesState } from './ducks/cities/types'
import { ClosingsState } from './ducks/closings/types'
import { ConferencesState } from './ducks/conferences/types'
import { GroupTreesState } from './ducks/groupTrees/types'
import { HotelsState } from './ducks/hotels/types'
import { IssuancesState } from './ducks/issuances/types'
import { PartnersState } from './ducks/partners/types'
import { PartnerHotelsState } from './ducks/partners/hotels/types'
import { BedroomsOfferState } from './ducks/partners/offer/types'
import { AgreementState } from './ducks/partners/agreement/types'
import { AgreementOfferState } from './ducks/partners/agreementsOffer/types'
import { BedroomsState } from './ducks/partners/bedrooms/types'
import { AgencyPartnersState } from './ducks/partners/link-agency/types'
import { MeState } from './ducks/me/types'
import { OrganizationsState } from './ducks/organizations/types'
import { RequestsState } from './ducks/requests/types'
import { ResourcesState } from './ducks/resources/types'
import { TreeState } from './ducks/tree/types'
import { UsersState } from './ducks/users/types'
import { VehiclesState } from './ducks/vehicles/types'

export interface ApplicationState {
  air: AirState
  approvals: ApprovalsState
  audits: AuditsState
  auth: AuthState
  cities: CitiesState
  closings: ClosingsState
  conferences: ConferencesState
  groupTrees: GroupTreesState
  hotels: HotelsState
  issuances: IssuancesState
  partners: PartnersState
  partner_hotels: PartnerHotelsState
  partner_hotel_rooms: BedroomsState
  agency_partners: AgencyPartnersState
  agency_partner_hotel_offers: BedroomsOfferState
  agreement: AgreementState
  agency_partner_hotel_agreements: AgreementOfferState
  me: MeState
  organizations: OrganizationsState
  requests: RequestsState
  resources: ResourcesState
  tree: TreeState
  users: UsersState
  vehicles: VehiclesState
}

type UseStoreProps<S> = {
  customMiddlewares?: any
  dev: boolean
  persistConfig: PersistConfig<S, any, any, any>
  reducers: ReducersMapObject<S, any>
  sagas: any[]
}

function store<S>({
  customMiddlewares = [],
  dev = true,
  persistConfig,
  reducers,
  sagas
}: UseStoreProps<S>) {
  const persistedReducer = persistReducer<S, Action<any>>(
    persistConfig,
    combineReducers(reducers)
  )

  const sagaMiddleware = createSagaMiddleware()

  const middlewares = [sagaMiddleware, ...customMiddlewares]

  const composer = compose(applyMiddleware(...middlewares))

  const createdStore: Store<S> = createStore(persistedReducer, composer)

  const persistor = persistStore(createdStore)

  sagaMiddleware.run(function* rootSaga() {
    return yield all(sagas)
  })

  return { persistor, store: createdStore }
}

export default store
